import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Pencil, Trash2, Package, AlertTriangle, Barcode, Gift, Wrench, Sparkles, Link2, MapPin } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UNITS = [
  { value: 'unidad', label: 'Unidad(es)' },
  { value: 'litro', label: 'Litro(s)' },
  { value: 'ml', label: 'Mililitro(s)' },
  { value: 'kg', label: 'Kilogramo(s)' },
  { value: 'g', label: 'Gramo(s)' },
  { value: 'caja', label: 'Caja(s)' },
  { value: 'paquete', label: 'Paquete(s)' },
];

const CATEGORIES = [
  { value: 'cortesia', label: 'Cortesía', icon: Gift, color: 'text-emerald-400', bgColor: 'bg-emerald-500/10' },
  { value: 'estacion', label: 'Estación', icon: Wrench, color: 'text-blue-400', bgColor: 'bg-blue-500/10' },
  { value: 'limpieza', label: 'Limpieza', icon: Sparkles, color: 'text-purple-400', bgColor: 'bg-purple-500/10' },
];

export default function AdminSupplies() {
  const { getAuthHeaders } = useAuth();
  const [supplies, setSupplies] = useState([]);
  const [products, setProducts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSupply, setEditingSupply] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    unit: 'unidad',
    stock: '0',
    min_stock: '5',
    cost: '0',
    barcode: '',
    category: 'cortesia',
    linked_product_id: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchSupplies();
  }, [activeCategory]);

  const fetchData = async () => {
    try {
      const [suppliesRes, productsRes, branchesRes] = await Promise.all([
        axios.get(`${API}/supplies`, getAuthHeaders()),
        axios.get(`${API}/products`, getAuthHeaders()),
        axios.get(`${API}/branches`, getAuthHeaders()),
      ]);
      setSupplies(suppliesRes.data);
      setProducts(productsRes.data);
      setBranches(branchesRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const fetchSupplies = async () => {
    try {
      const params = activeCategory !== 'all' ? `?category=${activeCategory}` : '';
      const response = await axios.get(`${API}/supplies${params}`, getAuthHeaders());
      setSupplies(response.data);
    } catch (error) {
      toast.error('Error al cargar insumos');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      stock: parseFloat(formData.stock) || 0,
      min_stock: parseFloat(formData.min_stock) || 0,
      cost: parseFloat(formData.cost) || 0,
      linked_product_id: formData.category === 'estacion' ? formData.linked_product_id : null,
    };

    try {
      if (editingSupply) {
        await axios.put(`${API}/supplies/${editingSupply.id}`, payload, getAuthHeaders());
        toast.success('Insumo actualizado');
      } else {
        await axios.post(`${API}/supplies`, payload, getAuthHeaders());
        toast.success('Insumo creado');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchSupplies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      unit: 'unidad',
      stock: '0',
      min_stock: '5',
      cost: '0',
      barcode: '',
      category: 'cortesia',
      linked_product_id: '',
    });
    setEditingSupply(null);
  };

  const handleEdit = (supply) => {
    setEditingSupply(supply);
    setFormData({
      name: supply.name,
      unit: supply.unit,
      stock: supply.stock.toString(),
      min_stock: supply.min_stock.toString(),
      cost: supply.cost.toString(),
      barcode: supply.barcode || '',
      category: supply.category || 'cortesia',
      linked_product_id: supply.linked_product_id || '',
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este insumo?')) return;
    try {
      await axios.delete(`${API}/supplies/${id}`, getAuthHeaders());
      toast.success('Insumo eliminado');
      fetchSupplies();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const isLowStock = (supply) => {
    return supply.stock <= supply.min_stock;
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-supplies">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl text-white tracking-tight">Insumos</h1>
            <p className="text-zinc-500 mt-1">Gestiona los insumos y materiales</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button 
                className="bg-gold text-black hover:bg-gold-dark"
                data-testid="add-supply-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Insumo
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl text-white">
                  {editingSupply ? 'Editar Insumo' : 'Nuevo Insumo'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400">Nombre</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Shampoo profesional"
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="supply-name-input"
                    required
                  />
                </div>
                
                {/* Category Selection */}
                <div className="space-y-2">
                  <Label className="text-zinc-400">Categoría</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value, linked_product_id: '' })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="supply-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      {CATEGORIES.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value} className="text-white">
                          <div className="flex items-center gap-2">
                            <cat.icon className={`w-4 h-4 ${cat.color}`} />
                            {cat.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-600">
                    {formData.category === 'cortesia' && 'Se descuenta del stock de insumos'}
                    {formData.category === 'estacion' && 'Se descuenta del stock de PRODUCTOS vinculados'}
                    {formData.category === 'limpieza' && 'Se descuenta del stock de insumos'}
                  </p>
                </div>
                
                {/* Linked Product (only for estacion) */}
                {formData.category === 'estacion' && (
                  <div className="space-y-2">
                    <Label className="text-zinc-400 flex items-center gap-2">
                      <Link2 className="w-4 h-4" />
                      Producto Vinculado
                    </Label>
                    <Select
                      value={formData.linked_product_id}
                      onValueChange={(value) => setFormData({ ...formData, linked_product_id: value })}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="supply-linked-product-select">
                        <SelectValue placeholder="Selecciona producto" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700 max-h-60">
                        {products.map((product) => (
                          <SelectItem key={product.id} value={product.id} className="text-white">
                            {product.name} (Stock: {product.stock || 0})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-blue-400">
                      Al usar este insumo, se descontará del stock del producto seleccionado
                    </p>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Unidad de Medida</Label>
                    <Select
                      value={formData.unit}
                      onValueChange={(value) => setFormData({ ...formData, unit: value })}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        {UNITS.map((unit) => (
                          <SelectItem key={unit.value} value={unit.value} className="text-white">
                            {unit.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Código de Barras</Label>
                    <Input
                      value={formData.barcode}
                      onChange={(e) => setFormData({ ...formData, barcode: e.target.value.toUpperCase() })}
                      placeholder="INS001"
                      className="bg-zinc-800 border-zinc-700 text-white font-mono"
                      data-testid="supply-barcode-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Stock Actual</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.stock}
                      onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-white"
                      data-testid="supply-stock-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Stock Mínimo</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.min_stock}
                      onChange={(e) => setFormData({ ...formData, min_stock: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-white"
                      data-testid="supply-min-stock-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">Costo por Unidad</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.cost}
                    onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                    placeholder="0.00"
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="supply-cost-input"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                    className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-gold text-black hover:bg-gold-dark"
                    data-testid="supply-submit-btn"
                    disabled={formData.category === 'estacion' && !formData.linked_product_id}
                  >
                    {editingSupply ? 'Actualizar' : 'Crear'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Low Stock Alert */}
        {supplies.some(isLowStock) && (
          <Card className="bg-yellow-500/10 border-yellow-500/30 p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              <div>
                <p className="text-yellow-500 font-medium">Insumos con Stock Bajo</p>
                <p className="text-zinc-400 text-sm">
                  {supplies.filter(isLowStock).map(s => s.name).join(', ')}
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Filters: Category Tabs and Branch Selector */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <Tabs value={activeCategory} onValueChange={setActiveCategory} className="flex-1">
            <TabsList className="bg-zinc-900 border border-zinc-800">
              <TabsTrigger 
                value="all" 
                className="data-[state=active]:bg-gold data-[state=active]:text-black"
              >
                Todos
              </TabsTrigger>
              {CATEGORIES.map((cat) => (
                <TabsTrigger 
                  key={cat.value}
                  value={cat.value}
                  className={`data-[state=active]:${cat.bgColor} data-[state=active]:${cat.color}`}
                >
                  <cat.icon className="w-4 h-4 mr-1" />
                  {cat.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          
          <div className="w-full sm:w-64">
            <Select value={selectedBranch} onValueChange={setSelectedBranch}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="supplies-branch-filter">
                <MapPin className="w-4 h-4 mr-2 text-zinc-500" />
                <SelectValue placeholder="Todas las sucursales" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="all" className="text-white">Todas las sucursales</SelectItem>
                {branches.map((branch) => (
                  <SelectItem key={branch.id} value={branch.id} className="text-white">
                    {branch.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Supplies Grid */}
        {loading ? (
          <div className="text-center py-12 text-zinc-500">Cargando...</div>
        ) : supplies.length === 0 ? (
          <Card className="bg-zinc-900/50 border-zinc-800 p-12 text-center">
            <Package className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500">No hay insumos registrados</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {supplies.map((supply) => {
              const categoryInfo = CATEGORIES.find(c => c.value === supply.category) || CATEGORIES[0];
              const CategoryIcon = categoryInfo.icon;
              
              return (
                <Card 
                  key={supply.id} 
                  className={`bg-zinc-900/50 border-zinc-800 p-5 hover:border-zinc-700 transition-colors ${
                    isLowStock(supply) ? 'border-l-4 border-l-yellow-500' : ''
                  }`}
                  data-testid={`supply-card-${supply.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 rounded-full ${categoryInfo.bgColor} flex items-center justify-center`}>
                      <CategoryIcon className={`w-6 h-6 ${categoryInfo.color}`} />
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleEdit(supply)}
                        className="w-7 h-7 rounded-sm bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => handleDelete(supply.id)}
                        className="w-7 h-7 rounded-sm bg-zinc-800 hover:bg-red-500/20 flex items-center justify-center text-zinc-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Category Badge */}
                  <div className="mb-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${categoryInfo.bgColor} ${categoryInfo.color}`}>
                      {categoryInfo.label}
                    </span>
                  </div>
                  
                  <h3 className="font-heading text-lg text-white mb-1">{supply.name}</h3>
                  
                  {/* Linked Product (for estacion) */}
                  {supply.category === 'estacion' && supply.linked_product_name && (
                    <p className="text-xs text-blue-400 flex items-center gap-1 mb-2">
                      <Link2 className="w-3 h-3" />
                      Vinculado: {supply.linked_product_name}
                    </p>
                  )}
                  
                  {supply.barcode && (
                    <p className="text-xs text-zinc-600 font-mono flex items-center gap-1 mb-3">
                      <Barcode className="w-3 h-3" />
                      {supply.barcode}
                    </p>
                  )}
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-zinc-500">Stock</span>
                      <span className={`font-semibold ${isLowStock(supply) ? 'text-yellow-500' : 'text-white'}`}>
                        {supply.stock} {supply.unit}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-zinc-500">Mínimo</span>
                      <span className="text-zinc-400">{supply.min_stock} {supply.unit}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-zinc-500">Costo</span>
                      <span className="text-gold font-semibold">${supply.cost.toFixed(2)}</span>
                    </div>
                  </div>
                  {isLowStock(supply) && (
                    <div className="mt-3 pt-3 border-t border-zinc-800">
                      <p className="text-xs text-yellow-500 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Stock bajo - Reabastecer
                      </p>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
